import { useContext, useEffect } from 'preact/hooks';
import { createRef } from 'preact';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { POLL_TYPES } from '../../helpers/constants';
import { getPollTypePartPrefix, joinParts } from '../../helpers/parts';
import { ShareButton } from '@playbook/sdk-helpers';

import styleSheet from './poll.module.scss';

import { enableEditableContent } from '../../helpers/editable-content';
import { PollContext } from '../../polls/poll.component';

import QuestionIcon from '../shared-components/icons/QuestionIcon';
import { useTranslation } from 'react-i18next';

const styles = styleSheet.locals || {};

/**
 * Component for the poll header
 *
 * @param {object} props poll option props
 * @param {object} props.poll poll option data
 * @param {boolean} props.pollScheduled if poll is scheduled
 * @returns {Function} <PollHeader />
 */
function PollHeader({ poll, pollScheduled = false }) {
    const { t } = useTranslation();
    const pollContext = useContext(PollContext);
    const question = poll.questions[0];
    const editableAttr = pollContext.isPreview ? { contentEditable: true } : {};
    const titleRef = createRef();
    const descrRef = createRef();

    const isTrivia =
        (poll?.type === POLL_TYPES.TRIVIA ||
            poll?.type === POLL_TYPES.FREE_TEXT) ??
        false;
    const pollPartPrefix = getPollTypePartPrefix(poll?.type);

    const changeEditableData = (field, value) => {
        pollContext.onPreviewEditChange?.({
            [field]: value,
            questions: [
                {
                    [field]: value,
                    options: []
                }
            ]
        });
    };

    useEffect(() => {
        if (pollContext.isPreview) {
            enableEditableContent(titleRef.current, 'text', changeEditableData);
            enableEditableContent(
                descrRef.current,
                'description',
                changeEditableData
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            part={joinParts('poll-header', `${pollPartPrefix}-header`)}
            className={classNames(styles.pollHeader, {
                [styles.pollHeaderScheduled]: pollScheduled && !isTrivia
            })}
        >
            <div
                part={joinParts('poll-prompt', `${pollPartPrefix}-prompt`)}
                className={classNames(styles.pollPrompt, {
                    [styles.pollPromptTrivia]: pollScheduled && isTrivia
                })}
            >
                {isTrivia && (
                    <div
                        className={styles.pollProductLabel}
                        part={joinParts(
                            'trivia-product-label',
                            `${pollPartPrefix}-product-label`
                        )}
                    >
                        <span
                            data-cy="QuestionIcon"
                            part={joinParts(
                                'trivia-product-icon',
                                `${pollPartPrefix}-product-icon`
                            )}
                        >
                            <QuestionIcon />
                        </span>

                        <span
                            data-cy="productLabel"
                            className={styles.pollProduct}
                            part={joinParts(
                                'trivia-product-text',
                                `${pollPartPrefix}-product-text`
                            )}
                        >
                            {t('trivia.productName').toUpperCase()}
                        </span>
                    </div>
                )}

                <span
                    part={joinParts(
                        'poll-question',
                        `${pollPartPrefix}-question`
                    )}
                    className={classNames(styles.pollQuestion, {
                        [styles.pollQuestionTrivia]: pollScheduled && isTrivia
                    })}
                    ref={titleRef}
                    data-fek-preview="text"
                    {...editableAttr}
                >
                    {question.text}
                </span>
                <div
                    part={joinParts(
                        'poll-description',
                        `${pollPartPrefix}-description`
                    )}
                    className={classNames(styles.pollDescription, {
                        [styles.pollDescriptionTrivia]:
                            pollScheduled && isTrivia
                    })}
                    ref={descrRef}
                    data-fek-preview="description"
                    {...editableAttr}
                >
                    {question.description}
                </div>
            </div>

            {!pollScheduled && (
                <ShareButton
                    pageId={isTrivia ? poll.trivia_id : poll.poll_id}
                    customUrl={
                        pollContext.isDynamicHosting
                            ? window.location.href
                            : null
                    }
                    copiedLabel={t('button.shareCopied')}
                    partPrefix={`${pollPartPrefix}-share`}
                    onCopied={() => {
                        globalThis.PLAYBOOK_CONFIG?.interactionEvent?.send(
                            isTrivia ? 'TRIVIA_SHARED' : 'POLL_SHARED'
                        );
                    }}
                />
            )}
        </div>
    );
}

PollHeader.propTypes = {
    poll: PropTypes.object.isRequired,
    pollScheduled: PropTypes.bool
};

export default PollHeader;
